<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Header -->
      <div class="flex items-center justify-between mb-8">
        <div>
          <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Your Organizations</h1>
          <p class="text-gray-600 dark:text-gray-400">
            Manage your organizations and teams or create a new one.
          </p>
        </div>
        <Button @click="showCreateModal = true">
          <PlusIcon class="h-5 w-5 mr-2" />
          Create Organization
        </Button>
      </div>

      <!-- Content -->
      <div v-if="isLoading && organizations.length === 0" class="flex justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>

      <div
        v-else-if="organizations.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        <OrganizationCard
          v-for="org in organizations"
          :key="org.id"
          :organization="org"
          :isActive="currentOrganizationId === org.id"
          :canManage="true"
          @select="handleSelect"
          @settings="handleSettings"
        />
      </div>

      <!-- eslint-disable-next-line asab-design-system/no-raw-button-card-markup -- dashed empty-state box, not a Card (no dashed/empty variant exists yet) -->
      <div
        v-else
        class="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700"
      >
        <BuildingOfficeIcon class="mx-auto h-12 w-12 text-gray-400" />
        <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No organizations</h3>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          You are not a member of any organizations yet.
        </p>
        <div class="mt-6">
          <Button @click="showCreateModal = true">
            <PlusIcon class="h-5 w-5 mr-2" />
            Create your first organization
          </Button>
        </div>
      </div>

      <!-- Modals -->
      <CreateOrganizationModal v-model:open="showCreateModal" @created="handleCreated" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { PlusIcon, BuildingOfficeIcon } from '@heroicons/vue/24/outline';
  import { useOrganizationContext } from '../composables';
  import { OrganizationCard, CreateOrganizationModal } from '../components';
  import { Button } from '@asabdev/asab-ui';

  const router = useRouter();
  const { organizations, currentOrganizationId, setCurrentOrganization, initialize, isLoading } =
    useOrganizationContext();

  const showCreateModal = ref(false);

  onMounted(async () => {
    await initialize();
  });

  const handleSelect = async (id: string) => {
    await setCurrentOrganization(id);
    router.push('/app');
  };

  const handleSettings = async (id: string) => {
    await setCurrentOrganization(id);
    router.push('/app/organizations/settings');
  };

  const handleCreated = async (id: string) => {
    await initialize();
    showCreateModal.value = false;
    // Automatically switch to the new org
    await handleSelect(id);
  };
</script>
